import React from "react";

import {
    QueryClient,
    QueryClientProvider,
    useInfiniteQuery,
    useMutation,
    useQuery,
} from "@tanstack/react-query";

import { isEmpty, isNumber, map, replace } from "lodash";

import { fetchInline } from "~/utilities/fetchHelper";

const queryClient = new QueryClient();

const oneHour = 60 * 60 * 1000;

const WP_GRAPHQL_URL = `${process.env.GATSBY_ADMIN_SITE_URL}/wp/graphql`;

/**
 * Takes in a graphql string and converts it to a string usable to post to the CMS
 * @param query string
 * @returns {string}
 */
const formatGraphQLForCms = (query) => {
    return replace(query, /Wp(?=.+)/gm, "");
};

const maybeThrowGraphQLError = (
    errors,
    errorMsg = "An error occurred while fetching data. Please try again.",
) => {
    if (isEmpty(errors)) {
        return null;
    }

    const graphqlErrors = map(errors, (error) => {
        return error.debugMessage ?? error.message;
    }).join("|");

    if (graphqlErrors) {
        console.error(graphqlErrors);
        throw new Error(errorMsg);
    }
};

export const QueryManagerProvider = ({ children }) => {
    return (
        <QueryClientProvider client={queryClient}>
            {children}
        </QueryClientProvider>
    );
};

export const useHTTPQuery = ({
    refetchOnWindowFocus = false,
    enabled = true,
    staleTime = oneHour,
    retry = 1,
    placeholderData,
    queryKey,
    queryFn,
    initialData,
    initialDataUpdatedAt,
}) => {
    const info = useQuery({
        refetchOnWindowFocus,
        staleTime,
        placeholderData,
        queryKey,
        queryFn,
        enabled,
        retry,
        initialData,
        initialDataUpdatedAt,
    });

    if (info === undefined) {
        throw new Error(
            "useHTTPQuery must be used within a QueryManagerProvider",
        );
    }

    return {
        data: info.data,
        error: info.error,
        isError: info.isError,
        isFetching: info.isFetching,
        status: info.status,
        refetch: info.refetch,
    };
};

export const useHTTPMutation = (parameters = {}) => {
    const {
        retry = 1,
        onSuccess,
        onError,
        onMutate,
        onSettled,
        scope,
        mutationFn,
    } = parameters;

    const mutationInfo = useMutation({
        mutationFn,
        onSuccess,
        onError,
        onMutate,
        onSettled,
        retry,
        scope,
    });

    if (mutationInfo === undefined) {
        throw new Error(
            "useHTTPMutation must be used within a QueryManagerProvider",
        );
    }

    return {
        isSuccess: mutationInfo.isSuccess,
        isError: mutationInfo.isError,
        status: mutationInfo.status,
        mutate: mutationInfo.mutate,
        data: mutationInfo.data,
        error: mutationInfo.error,
    };
};

export const useGraphQLQuery = (parameters = {}) => {
    const {
        query,
        variables = {},
        queryIdentifier = "UseGraphQLQuery",
        refetchOnWindowFocus = false,
        enabled = true,
        staleTime = oneHour,
        retry = 1,
        placeholderData,
        headers = {},
        errorMsg,
    } = parameters;

    const queryKey = [queryIdentifier, query, variables];

    const queryFn = async () => {
        const fetchOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...headers,
            },
            body: JSON.stringify({
                query: formatGraphQLForCms(query),
                variables,
            }),
        };

        const queryData = await fetchInline(WP_GRAPHQL_URL, fetchOptions);

        maybeThrowGraphQLError(queryData?.errors, errorMsg);

        return queryData;
    };

    const info = useQuery({
        refetchOnWindowFocus,
        staleTime,
        placeholderData,
        queryKey,
        queryFn,
        enabled,
        retry,
    });

    if (info === undefined) {
        throw new Error(
            "useGraphQLQuery must be used within a QueryManagerProvider",
        );
    }

    return {
        queryKey,
        queryFn,
        data: info.data,
        error: info.error,
        isError: info.isError,
        isFetching: info.isFetching,
        status: info.status,
        refetch: info.refetch,
    };
};

export const useInfiniteGraphQLQuery = (parameters = {}) => {
    const {
        query,
        variables = {},
        queryIdentifier = "UseInfiniteGraphQLQuery",
        refetchOnWindowFocus = false,
        enabled = true,
        staleTime = oneHour,
        retry = 1,
        placeholderData,
        getNextPageParam,
        headers = {},
        errorMsg,
    } = parameters;

    const queryKey = [queryIdentifier, query, variables];

    const queryFn = async ({ pageParam }) => {
        const newVariables = {
            ...variables,
            first: isNumber(pageParam) ? pageParam : variables.first,
        };

        const fetchOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...headers,
            },
            body: JSON.stringify({
                query: formatGraphQLForCms(query),
                variables: newVariables,
            }),
        };

        const queryData = await fetchInline(WP_GRAPHQL_URL, fetchOptions);

        maybeThrowGraphQLError(queryData?.errors, errorMsg);

        return queryData;
    };

    const info = useInfiniteQuery({
        queryKey,
        queryFn,
        refetchOnWindowFocus,
        enabled,
        staleTime,
        retry,
        placeholderData,
        initialPageParam: isNumber(variables.first) ? variables.first : 1,
        getNextPageParam,
    });

    if (info === undefined) {
        throw new Error(
            "useInfiniteGraphQLQuery must be used within a QueryManagerProvider",
        );
    }

    return {
        queryKey,
        data: info.data,
        error: info.error,
        isError: info.isError,
        isFetching: info.isFetching,
        status: info.status,
        refetch: info.refetch,
        isFetchingNextPage: info.isFetchingNextPage,
        hasNextPage: info.hasNextPage,
        fetchNextPage: info.fetchNextPage,
    };
};

export const useGraphQLMutation = (parameters = {}) => {
    const {
        query,
        retry = 1,
        onSuccess,
        onError,
        onMutate,
        onSettled,
        scope,
        headers = {},
        errorMsg,
    } = parameters;

    const mutationFn = async (variables) => {
        const fetchOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...headers,
            },
        };

        if (headers["Content-Type"] === "multipart/form-data") {
            delete fetchOptions.headers["Content-Type"];
            const data = new FormData();
            data.append(
                "operations",
                JSON.stringify({
                    query: formatGraphQLForCms(query),
                    variables: {
                        file: null,
                    },
                }),
            );
            data.append("map", JSON.stringify({ 0: ["variables.file"] }));
            data.append("0", variables.file);
            fetchOptions.body = data;
        } else {
            fetchOptions.body = JSON.stringify({
                query: formatGraphQLForCms(query),
                variables,
            });
        }

        const mutation = await fetchInline(WP_GRAPHQL_URL, fetchOptions);

        maybeThrowGraphQLError(mutation?.errors, errorMsg);

        return mutation;
    };

    const mutationInfo = useMutation({
        mutationFn,
        onSuccess,
        onError,
        onMutate,
        onSettled,
        retry,
        scope,
    });

    if (mutationInfo === undefined) {
        throw new Error(
            "useGraphQLMutation must be used within a QueryManagerProvider",
        );
    }

    return {
        isSuccess: mutationInfo.isSuccess,
        isError: mutationInfo.isError,
        status: mutationInfo.status,
        mutate: mutationInfo.mutate,
        data: mutationInfo.data,
        error: mutationInfo.error,
    };
};
